<template>
  <LineChart
    :request-status="requestStatus"
    :series="series"
    :categories="categories"
    :title="title"
    :item-id="itemId"
    class="make-a-copy"
    :card-options="cardOptions"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import LineChart from '@/components/cards/models/LineChart';
import cardsModel from '@/mixins/cards-model';
import { ENTITIES, POST_FIELDS, OPERATION, VALUE_TYPES } from '@/services/charts-service';
import { DATA_TYPE } from '@/store/modules/cards/constants';
import { msToTime } from '@/utils/date';
import EventBus from '@/utils/event-bus';
import { mapActions } from 'vuex';

export default {
  name: 'TotalPostsSharesAndCommentsInTime',
  components: {
    LineChart,
  },
  mixins: [
    cardsModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    metric: {
      type: [String, Array],
      default: null,
    },
    entity: {
      type: String,
      default: ENTITIES.POSTS,
    },
    label: {
      type: String,
      default: null,
    },
    operation: {
      type: String,
      default: OPERATION.COUNT,
    },
    valueType: {
      type: String,
      default: null,
    },
    ownerPosts: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: [],
      classifiedPercentage: 0,
      cardOptions: {
        chart: {
          type: 'areaspline',
        },
        plotOptions: {
          areaspline: {
            fillOpacity: 0.05,
            lineWidth: 1,
            marker: {
              radius: 2,
            },
          },
        },
        legend: false,
        ...(
          this.valueType === VALUE_TYPES.TIME
            ? {
              yAxis: {
                type: 'datetime',
                labels: {
                  formatter() {
                    return msToTime(this.value);
                  },
                },
              },
              tooltip: {
                pointFormatter() {
                  const { y, color, series } = this;
                  const value = msToTime(y);
                  return `<span style="color:${color}">●</span> ${series.name}: <b>${value}</b><br/>`;
                },
              },
            }
            : null
        ),
      },
    };
  },
  computed: {
    formattedData() {
      return this.rawSeries?.points?.map(({ x, z: y }) => ({ x, y }));
    },
    series() {
      return this.rawSeries;
    },
    categories() {
      if (!this.isQuarterOnDatePortion) {
        return null;
      }

      return this.formattedData?.map(({ x }) => this.formattedLabelForQuarter(x));
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
    ]),
    getData(options) {
      let fields;

      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      if (this.metric) {
        fields = [this.metric];
      }

      const promises = [
        this.getChartsService({
          itemId: this.itemId,
          method: 'getTimeSeries',
          points_parser: 'points',
          entity: this.entity,
          filterType: DATA_TYPE.LISTENING,
          params: {
            fields,
            operators: this.operation,
            datePortion: this.getDatePortion(options),
          },
        }),
        this.getChartsService({
          itemId: this.itemId,
          method: 'getTimeSeries',
          points_parser: 'points',
          entity: this.entity,
          filterType: DATA_TYPE.SAC,
          params: {
            fields,
            operators: this.operation,
            datePortion: this.getDatePortion(options),
          },
        }),
        this.getChartsService({
          itemId: this.itemId,
          method: 'getTimeSeries',
          points_parser: 'points',
          entity: this.entity,
          filterType: DATA_TYPE.LISTENING,
          params: {
            fields: [POST_FIELDS.COMMENTS],
            operators: OPERATION.SUM,
            datePortion: this.getDatePortion(options),
          },
        }),
        this.getChartsService({
          itemId: this.itemId,
          method: 'getTimeSeries',
          points_parser: 'points',
          entity: this.entity,
          filterType: DATA_TYPE.SAC,
          params: {
            fields: [POST_FIELDS.COMMENTS],
            operators: OPERATION.SUM,
            datePortion: this.getDatePortion(options),
          },
        }),
        this.getChartsService({
          itemId: this.itemId,
          method: 'getTimeSeries',
          points_parser: 'points',
          entity: this.entity,
          filterType: DATA_TYPE.LISTENING,
          params: {
            fields: [POST_FIELDS.SHARES],
            operators: OPERATION.SUM,
            datePortion: this.getDatePortion(options),
          },
        }),
        this.getChartsService({
          itemId: this.itemId,
          method: 'getTimeSeries',
          points_parser: 'points',
          entity: this.entity,
          filterType: DATA_TYPE.SAC,
          params: {
            fields: [POST_FIELDS.SHARES],
            operators: OPERATION.SUM,
            datePortion: this.getDatePortion(options),
          },
        }),
      ];

      Promise.all(promises)
        .then((responses) => {
          const seriesSum = this.aggregateChartResponses(responses);
          this.rawSeries = seriesSum;
          this.requestStatus = 'success';

          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
        }).catch(error => {
          console.error(error);
          this.requestStatus = 'error';
        });
    },
    parseData(data) {
      return this.isQuarterOnDatePortion ? data?.map(({ y }) => y) : data?.data;
    },
    aggregateChartResponses(chartResponses) {
      const chartNames = [
        'Publicações não proprietárias',
        'Publicações proprietárias',
        'Comentários não proprietários',
        'Comentários proprietários',
        'Compartilhamentos não proprietários',
        'Compartilhamentos proprietários',
      ];
      const individualSeries = chartResponses.map((chartRes, idx) => ({
        name: chartNames[idx],
        data: chartRes.points.map(p => ({ x: p.x, y: p.z })),
        axes: chartResponses[0]?.axes,
      }));

      const pointsMap = new Map();
      chartResponses.forEach(chartRes => {
        chartRes.points.forEach(p => {
          pointsMap.set(p.x, (pointsMap.get(p.x) || 0) + p.z);
        });
      });

      const aggregateData = [];
      for (const key of pointsMap.keys()) {
        aggregateData.push({ x: key, y: pointsMap.get(key) });
      }

      const aggregateSeries = {
        name: this.title,
        data: aggregateData,
        axes: chartResponses[0]?.axes,
      };

      return [aggregateSeries, ...individualSeries];
    },
  },
};
</script>
